import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, Checkbox } from '@/components/v2/ui'
import { useStartListingRemake } from '@/hooks/useListingBuilder'
import { EID } from '@/lib/element-ids.registry'

import { MarketplaceSelector } from '../MarketplaceSelector'

const FIELD_OPTIONS = [
  { key: 'title', eid: EID['listing-builder-remake-field-title'] },
  { key: 'bullets', eid: EID['listing-builder-remake-field-bullets'] },
  { key: 'description', eid: EID['listing-builder-remake-field-description'] },
] as const

interface RemakeStarterProps {
  jobId: string
}

/** Marketplace + optional partial-field selection + start button for a remake. */
export function RemakeStarter({ jobId }: RemakeStarterProps) {
  const { t } = useTranslation()
  const [marketplaceCode, setMarketplaceCode] = useState('')
  const [fields, setFields] = useState<string[]>([])
  const startRemake = useStartListingRemake(jobId)

  const toggleField = (key: string) =>
    setFields((prev) => (prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key]))

  const onStart = () =>
    startRemake.mutate({
      marketplace_code: marketplaceCode,
      fields: fields.length ? fields : undefined,
    })

  return (
    <section className="listing-builder-remake-starter space-y-4" aria-label={t('listingBuilder.remake.starterTitle')}>
      <MarketplaceSelector value={marketplaceCode} onChange={setMarketplaceCode} />
      <fieldset className="listing-builder-remake-fields space-y-2">
        <legend className="text-sm font-medium">{t('listingBuilder.remake.fieldsLegend')}</legend>
        {FIELD_OPTIONS.map(({ key, eid }) => (
          <Checkbox
            key={key}
            id={eid}
            label={t(`listingBuilder.remake.fields.${key}`)}
            checked={fields.includes(key)}
            onChange={() => toggleField(key)}
          />
        ))}
      </fieldset>
      <Button
        id={EID['listing-builder-start-remake-btn']}
        type="button"
        variant="primary"
        disabled={!marketplaceCode || startRemake.isPending}
        onClick={onStart}
      >
        {t('listingBuilder.remake.startButton')}
      </Button>
    </section>
  )
}
